import { Button, Typography, Container, Card, CardContent, Alert, Box } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_CONFIG } from '../configuration/api';

const DeleteConfirmation: React.FC = () => {
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [hasConfirmed, setHasConfirmed] = useState(false);
    const { id } = useParams<{ id: string }>();

    useEffect(() => {
        if (!id) {
            setMessage('Invalid deletion request URL.');
        }
    }, [id]);

    const handleDeleteRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!id) {
            setMessage('Invalid deletion request.');
            return;
        }

        setIsLoading(true);
        setMessage('');
        
        try {   
            const response = await fetch(API_CONFIG.getDataUrl(false), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    id: id,
                    action: 'confirm_deletion'
                }),
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setMessage('Your data has been successfully deleted from our systems. You will receive a final confirmation email shortly.');
                    setIsSuccess(true);
                    setHasConfirmed(true);
                } else {
                    setMessage(result.message || 'There was an error processing your deletion request.');
                    setIsSuccess(false);
                }
            } else {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const errorData = await response.json();
                setMessage('There was an error processing your deletion request. Please contact us at privacy@famousinitials.com');
                setIsSuccess(false);
            }
        } catch (error) {
            setMessage('There was an error processing your deletion request. Please contact us at privacy@famousinitials.com');
            setIsSuccess(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: { xs: 0.25, sm: 0.2, md: 1.5 }, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
            <Card elevation={3} sx={{ maxWidth: 600, mx: 'auto' }}>
                <CardContent sx={{ p: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom align="center" color="error">
                        Confirm Data Deletion
                    </Typography>
                    
                    {!hasConfirmed ? (
                        <>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                                You are about to permanently delete all your data from Famous Initials. This includes:
                            </Typography>
                            
                            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
                                <li>Your user account and profile information</li>
                                <li>All game progress and scores</li>
                                <li>Your login credentials (including Google/Facebook connections)</li>
                                <li>Any other personal data associated with your account</li>
                            </Box>
                            
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                <strong>This action cannot be undone.</strong> Once confirmed, all your data will be permanently removed from our systems.
                            </Alert>

                            <Box component="form" onSubmit={handleDeleteRequest}>
                                <Button 
                                    type="submit" 
                                    variant="contained" 
                                    color="error"
                                    size="large"
                                    fullWidth
                                    disabled={isLoading || !id}
                                    sx={{ mb: 2 }}
                                >
                                    {isLoading ? 'Processing Deletion...' : 'Confirm - Delete All My Data'}
                                </Button>
                            </Box>
                        </>
                    ) : null}

                    {message && (
                        <Alert 
                            severity={isSuccess ? 'success' : 'error'} 
                            sx={{ mt: 2 }}
                        >
                            {message}
                        </Alert>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                        Need help? Contact us at{' '}
                        <Typography component="a" href="mailto:privacy@famousinitials.com" color="primary">
                            privacy@famousinitials.com
                        </Typography>
                    </Typography>
                </CardContent>
            </Card>
        </Container>
    );
};

export default DeleteConfirmation;